import { generateSlug, sortBlogByDateDesc } from '../blog-helpers';
import type { BlogPostLoader, BlogPostMeta } from './types';

/** English title keys — used for stable URL slugs and tag extraction (language-independent). */
export const TK = {
    aiThreats: 'Defence Against AI Threats; How to Identify, Analyze, and Reduce Risks',
    cloudFiles: "Cloud Files at Risk: Here's How to Keep Them Protected",
    communitySafe: 'MetaDefender Cloud Community; A Safe Place for Malware Analysis & Collaboration',
    saferInternet: 'Building a Safer Internet Together: How You Can Contribute to MetaDefender Cloud Community',
    multiEngine: "The Rise of Multi-Engine Scanning: Why One Engine Isn't Enough Anymore",
    urlScanners: 'Free URL & Link Scanners: How to Detect Phishing Before You Click',
    fileScans100k: 'What +100k File Scans Reveal About Everyday File-Based Cyber Risks',
    fileTransit: 'Why File Security in Transit Is Essential for Modern Networks',
    storageCloud: 'Secure Your Storage Data with MetaDefender Storage Security Cloud; Demo Available',
    secureChromeEdgeDownloads: 'How to Secure Chrome and Edge Downloads Before Malware Reaches Your Device',
    chat3Creators: 'Chat3 Strengthens Platform Security for +1M Creators with OPSWAT',
    grafanaGhost: 'What GrafanaGhost Teaches Us About Blind Trust in AI-Driven Workflows',
    vtAlternative: 'MetaDefender Cloud™ as a Prevention-Focused VirusTotal Alternative',
    securePipelineStudio: 'How to Create and Configure Custom Workflows in MetaDefender Cloud™',
    staticVsDynamicAnalysis: "Static vs. Dynamic Analysis: How to Close the Malware Detection Gap Before It's Too Late",
    deepCdr: 'What is Content Disarm and Reconstruction (CDR)? How File Sanitization Prevents Zero-Day Threats',
    mastraNpm: 'North Korean State Actor Compromises 140+ @mastra AI Framework Packages in Coordinated NPM Supply Chain Attack',
} as const;

const slugCloudFiles = generateSlug(TK.cloudFiles);
const slugCommunitySafe = generateSlug(TK.communitySafe);
const slugSaferInternet = generateSlug(TK.saferInternet);
const slugMultiEngine = generateSlug(TK.multiEngine);
const slugUrlScanners = generateSlug(TK.urlScanners);
const slugFileScans100k = generateSlug(TK.fileScans100k);
const slugFileTransit = generateSlug(TK.fileTransit);
const slugStorageCloud = generateSlug(TK.storageCloud);
const slugAiThreats = generateSlug(TK.aiThreats);
const slugSecureChromeEdgeDownloads = generateSlug(TK.secureChromeEdgeDownloads);
const slugChat3Creators = generateSlug(TK.chat3Creators);
const slugGrafanaGhost = generateSlug(TK.grafanaGhost);
const slugVtAlternative = generateSlug(TK.vtAlternative);
const slugSecurePipelineStudio = generateSlug(TK.securePipelineStudio);
const slugStaticVsDynamicAnalysis = generateSlug(TK.staticVsDynamicAnalysis);
// Custom SEO slugs — shorter than the generated title slug. Exported so the
// post loaders reuse the exact same value (single source of truth).
export const slugDeepCdr = 'content-disarm-reconstruction-cdr-file-security';
export const slugMastraNpm = 'mastra-npm-supply-chain-attack';

const BLOG_ENTRIES: BlogPostMeta[] = [
    {
        slug: slugCloudFiles,
        titleEn: TK.cloudFiles,
        date: 'August 20th 2025',
        coverImage: '/image-1.jpg',
    },
    {
        slug: slugCommunitySafe,
        titleEn: TK.communitySafe,
        date: 'September 20th 2025',
        coverImage: '/image-2.jpg',
    },
    {
        slug: slugSaferInternet,
        titleEn: TK.saferInternet,
        date: 'November 5th 2025',
        coverImage: '/image-3.jpg',
    },
    {
        slug: slugMultiEngine,
        titleEn: TK.multiEngine,
        date: 'December 17th 2025',
        coverImage: '/frame-4.png',
    },
    {
        slug: slugUrlScanners,
        titleEn: TK.urlScanners,
        date: 'January 15th 2026',
        coverImage: '/image-4.jpg',
    },
    {
        slug: slugFileScans100k,
        titleEn: TK.fileScans100k,
        date: 'February 16th 2026',
        coverImage: '/image-5.jpg',
    },
    {
        slug: slugFileTransit,
        titleEn: TK.fileTransit,
        date: 'March 2nd 2026',
        coverImage: '/image-6.jpg',
    },
    {
        slug: slugStorageCloud,
        titleEn: TK.storageCloud,
        date: 'March 25th 2026',
        coverImage: '/image-7.jpg',
    },
    {
        slug: slugAiThreats,
        titleEn: TK.aiThreats,
        date: 'April 6th 2026',
        coverImage: '/image-8.jpg',
    },
    {
        slug: slugSecureChromeEdgeDownloads,
        titleEn: TK.secureChromeEdgeDownloads,
        date: 'April 24th 2026',
        coverImage: '/image-10.png',
    },
    {
        slug: slugChat3Creators,
        titleEn: TK.chat3Creators,
        date: 'April 29th 2026',
        coverImage: '/image-9.jpg',
    },
    {
        slug: slugGrafanaGhost,
        titleEn: TK.grafanaGhost,
        date: 'May 15th 2026',
        coverImage: '/image-12.jpg',
    },
    {
        slug: slugVtAlternative,
        titleEn: TK.vtAlternative,
        date: 'June 2nd 2026',
        coverImage: '/virusTotal.png',
    },
    {
        slug: slugSecurePipelineStudio,
        titleEn: TK.securePipelineStudio,
        date: 'June 8th 2026',
        coverImage: '/blog%2014.png',
    },
    {
        slug: slugStaticVsDynamicAnalysis,
        titleEn: TK.staticVsDynamicAnalysis,
        date: 'June 15th 2026',
        coverImage: '/blog15.png',
    },
    {
        slug: slugDeepCdr,
        titleEn: TK.deepCdr,
        date: 'July 6th 2026',
        coverImage: '/images/blog/blog16-preview.png',
        seoTitle: 'Deep CDR™: File Security Beyond Detection',
        seoDescription:
            'See how content disarm and reconstruction (CDR) neutralizes file-borne malware and zero-days. Try Deep CDR™ for free.',
    },
    {
        slug: slugMastraNpm,
        titleEn: TK.mastraNpm,
        date: 'July 8th 2026',
        coverImage: '/images/blog/blog-attack-NPM.png',
        seoTitle: 'Mastra NPM Supply Chain Attack: 140+ AI Packages Compromised',
        seoDescription:
            'Learn how the Mastra npm supply chain attack compromised 140+ AI packages, exposed CI/CD pipelines, and why behavioral analysis helps detect threats signatures miss.',
    },
];

export const BLOG_INDEX: BlogPostMeta[] = sortBlogByDateDesc(BLOG_ENTRIES);

export const BLOG_LOADERS: Record<string, BlogPostLoader> = {
    [slugCloudFiles]: (t) => import('./posts/cloud-files').then((m) => m.load(t)),
    [slugCommunitySafe]: (t) => import('./posts/community-safe').then((m) => m.load(t)),
    [slugSaferInternet]: (t) => import('./posts/safer-internet').then((m) => m.load(t)),
    [slugMultiEngine]: (t) => import('./posts/multi-engine').then((m) => m.load(t)),
    [slugUrlScanners]: (t) => import('./posts/url-scanners').then((m) => m.load(t)),
    [slugFileScans100k]: (t) => import('./posts/100k-file-scans').then((m) => m.load(t)),
    [slugFileTransit]: (t) => import('./posts/file-security-transit').then((m) => m.load(t)),
    [slugStorageCloud]: (t) => import('./posts/storage-security-cloud').then((m) => m.load(t)),
    [slugAiThreats]: (t) => import('./posts/ai-threats').then((m) => m.load(t)),
    [slugSecureChromeEdgeDownloads]: (t) => import('./posts/secure-downloads').then((m) => m.load(t)),
    [slugChat3Creators]: (t) => import('./posts/chat3-creators').then((m) => m.load(t)),
    [slugGrafanaGhost]: (t) => import('./posts/grafana-ghost').then((m) => m.load(t)),
    [slugVtAlternative]: (t) => import('./posts/vt-alternative').then((m) => m.load(t)),
    [slugSecurePipelineStudio]: (t) => import('./posts/secure-pipeline-studio').then((m) => m.load(t)),
    [slugStaticVsDynamicAnalysis]: (t) => import('./posts/static-vs-dynamic-analysis').then((m) => m.load(t)),
    [slugDeepCdr]: (t) => import('./posts/deep-cdr').then((m) => m.load(t)),
    [slugMastraNpm]: (t) => import('./posts/mastra-npm-attack').then((m) => m.load(t)),
};

export const getBlogMetaBySlug = (slug: string): BlogPostMeta | undefined => BLOG_INDEX.find((p) => p.slug === slug);

/**
 * Stable, language-independent OG image per blog slug. Used by social
 * crawlers (LinkedIn/Facebook/X) that fetch the page server-side and need a
 * deterministic absolute URL.
 */
export const getImageForPostSlug = (slug: string, siteUrl: string): string => {
    const path = (() => {
        if (slug.includes('free-url') || slug.includes('phishing')) {
            return '/images/blog/Free-URL-Link-Scanners-1.png';
        }
        if (slug.includes('cloud-files-at-risk') || slug.includes('what-100k-file-scans')) {
            return '/images/blog/are-your-cloud-files-secure-1.jpg';
        }
        if (slug.includes('building-a-safer-internet') || slug.includes('metadefender-cloud-community-a-safe')) {
            return '/images/blog/building-a-safer-internet-1.png';
        }
        if (slug.includes('multi-engine') || slug.includes('isnt-enough')) {
            return '/images/blog/infected-scan.png';
        }
        if (slug.includes('why-file-security-in-transit')) {
            return '/images/blog/file-security-transit-1.png';
        }
        if (slug.includes('secure-your-storage-data')) {
            return '/images/blog/storage-data-1.png';
        }
        const meta = getBlogMetaBySlug(slug);
        if (meta?.coverImage) {
            return meta.coverImage;
        }
        return '/images/blog/homepage.png';
    })();
    return `${siteUrl}${path}`;
};
